
import React, { useState } from 'react';
import { Users, ExternalLink, Code, Shield, Star, Sparkles, CheckCircle2, Lock, ArrowRight, Send, MessageSquare, Calendar, BookOpen } from 'lucide-react';
import { User, SubjectCategory } from '../types';
import { TEXT } from '../constants';
import { motion, AnimatePresence } from 'framer-motion';

interface TutorApplicationProps {
  user: User | null;
  onAuthClick: () => void;
  onNavigateToResources: () => void;
}

const TutorApplication: React.FC<TutorApplicationProps> = ({ user, onAuthClick, onNavigateToResources }) => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState<'tutor' | 'student'>('tutor');
  
  const qualifiedSubjects = user?.qualifiedSubjects || [];
  const isEligible = qualifiedSubjects.length > 0;

  const steps = [
    {
      title: 'Мэдлэгээ баталгаажуулах',
      desc: 'Номын сангаас дурын сэдвээ сонгож, асуулгыг 80%-иас дээш амжилттай бөглөнө.',
      icon: <Star size={24} />,
      status: qualifiedSubjects.length > 0 ? 'completed' : 'pending'
    },
    {
      title: 'Үе тэнгийн багш болох',
      desc: 'Баталгаажсан сэдвээрээ бусад сурагчдад туслах эрх нээгдэнэ.',
      icon: <Users size={24} />,
      status: qualifiedSubjects.length > 0 ? 'active' : 'locked'
    },
    {
      title: 'Нийгэмлэгт хувь нэмэр оруулах',
      desc: 'Мэдлэгээ хуваалцаж, Anir нийгэмлэгийн идэвхтэй гишүүн болно.',
      icon: <Sparkles size={24} />,
      status: 'locked'
    }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitted(true);
    setTimeout(() => setFormSubmitted(false), 5000);
  };

  return (
    <div className="w-full space-y-24 animate-in fade-in duration-500 text-left pb-40 relative">
      <header className="space-y-8 border-b border-slate-500/10 pb-16">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-8">
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-primary">
              <Sparkles size={20} />
              <span className="text-[10px] font-bold uppercase tracking-[0.4em]">Үе тэнгийнхний дэмжлэг</span>
            </div>
            <h1 className="text-6xl font-serif text-primary tracking-tighter leading-none">Багшлах / Тусламж авах</h1>
            <p className="text-muted text-sm font-bold uppercase tracking-[0.5em] leading-relaxed max-w-4xl">
              Anir бол сурагчдын санаачилга юм. Та өөрийн сайн мэддэг хичээлээрээ бусдад тусалж, эсвэл туршлагатай үе тэнгийнхнээсээ тусламж авах боломжтой.
            </p>
          </div>
          
          <div className="flex bg-slate-100 p-1.5 rounded-full border border-slate-200">
            <button 
              onClick={() => setActiveTab('tutor')}
              className={`px-8 py-3 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all ${activeTab === 'tutor' ? 'bg-white text-slate-900 shadow-md' : 'text-slate-500 hover:text-slate-800'}`}
            >
              Багшлах
            </button>
            <button 
              onClick={() => setActiveTab('student')}
              className={`px-8 py-3 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all ${activeTab === 'student' ? 'bg-white text-slate-900 shadow-md' : 'text-slate-500 hover:text-slate-800'}`}
            >
              Тусламж авах
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence mode="wait">
        {activeTab === 'tutor' ? (
          <motion.div 
            key="tutor-tab"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-24"
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {steps.map((step, i) => (
                <div key={i} className={`p-10 rounded-[3rem] border transition-all space-y-6 ${
                  step.status === 'completed' ? 'bg-emerald-50 border-emerald-100' :
                  step.status === 'active' ? 'bg-white border-primary shadow-xl scale-105' :
                  'bg-slate-50 border-slate-100 opacity-60'
                }`}>
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                    step.status === 'completed' ? 'bg-emerald-500 text-white' :
                    step.status === 'active' ? 'bg-primary text-white' :
                    'bg-slate-200 text-slate-500'
                  }`}>
                    {step.status === 'completed' ? <CheckCircle2 size={24} /> : step.icon}
                  </div>
                  <div className="space-y-2">
                    <h4 className="text-xl font-serif">{step.title}</h4>
                    <p className="text-xs font-medium text-muted leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <section className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div className="space-y-12">
                <div className="space-y-6">
                  <h3 className="text-4xl font-serif tracking-tight">Үе тэнгийн багшийн өргөдөл</h3>
                  <p className="text-muted text-lg font-medium leading-relaxed">
                    Та өөрийн мэдлэгээ баталгаажуулсан бол доорх өргөдлийг бөглөж манай багт нэгдээрэй. Бид таны мэдээллийг хянаж үзээд 48 цагийн дотор хариу өгөх болно.
                  </p>
                </div>

                <div className="space-y-8">
                  <div className="flex items-start gap-6">
                    <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary shrink-0">
                      <Shield size={24} />
                    </div>
                    <div className="space-y-1">
                      <h4 className="text-xl font-serif">Баталгаажсан байх</h4>
                      <p className="text-sm text-muted font-medium">Өргөдөл гаргахын өмнө дор хаяж нэг хичээлээр 80%-иас дээш оноо авсан байх шаардлагатай.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-6">
                    <div className="w-12 h-12 bg-secondary/10 rounded-2xl flex items-center justify-center text-secondary shrink-0">
                      <Calendar size={24} />
                    </div>
                    <div className="space-y-1">
                      <h4 className="text-xl font-serif">Цагийн хуваарь</h4>
                      <p className="text-sm text-muted font-medium">Долоо хоногт дор хаяж 2 цагийг бусдад туслахад зарцуулах боломжтой байх.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-12 rounded-[4rem] border border-slate-100 shadow-2xl relative">
                {!user ? (
                  <div className="text-center py-12 space-y-6">
                    <Lock size={48} className="mx-auto text-slate-300" />
                    <div className="space-y-2">
                      <h4 className="text-2xl font-serif">Нэвтрэх шаардлагатай</h4>
                      <p className="text-muted font-medium">Өргөдөл гаргахын тулд эхлээд системд нэвтэрнэ үү.</p>
                    </div>
                    <button 
                      onClick={onAuthClick}
                      className="px-10 py-4 bg-primary text-white rounded-full font-bold uppercase tracking-widest text-[10px]"
                    >
                      Нэвтрэх
                    </button>
                  </div>
                ) : !isEligible ? (
                  <div className="text-center py-12 space-y-6">
                    <Star size={48} className="mx-auto text-amber-400" />
                    <div className="space-y-2">
                      <h4 className="text-2xl font-serif">Мэдлэгээ баталгаажуулна уу</h4>
                      <p className="text-muted font-medium">Та одоогоор ямар нэгэн хичээлээр баталгаажаагүй байна.</p>
                    </div>
                    <button 
                      onClick={onNavigateToResources}
                      className="px-10 py-4 bg-slate-900 text-white rounded-full font-bold uppercase tracking-widest text-[10px] flex items-center gap-3 mx-auto"
                    >
                      Номын сан руу очих <ArrowRight size={14} />
                    </button>
                  </div>
                ) : formSubmitted ? (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12 space-y-6"
                  >
                    <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                      <CheckCircle2 size={40} />
                    </div>
                    <div className="space-y-2">
                      <h4 className="text-3xl font-serif text-emerald-600">Амжилттай илгээгдлээ</h4>
                      <p className="text-muted font-medium">Бид таны өргөдлийг хүлээн авлаа. Танд удахгүй хариу өгөх болно.</p>
                    </div>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-8">
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Баталгаажсан хичээлүүд</label>
                        <div className="flex flex-wrap gap-2">
                          {qualifiedSubjects.map(sub => (
                            <span key={sub} className="px-4 py-2 bg-primary/10 text-primary rounded-full text-[10px] font-bold uppercase tracking-widest">
                              {sub}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Таны туршлага</label>
                        <textarea 
                          required
                          placeholder="Та яагаад үе тэнгийн багш болохыг хүсэж байна вэ?"
                          className="w-full p-6 bg-slate-50 border border-slate-100 rounded-3xl outline-none focus:border-primary transition-all min-h-[150px] font-medium"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Холбоо барих мэдээлэл</label>
                        <input 
                          type="text"
                          defaultValue={user.phoneNumber}
                          disabled
                          className="w-full p-6 bg-slate-50 border border-slate-100 rounded-3xl outline-none opacity-50 font-medium"
                        />
                      </div>
                    </div>
                    <button 
                      type="submit"
                      className="w-full py-6 bg-slate-900 text-white rounded-full font-bold uppercase tracking-widest text-[10px] hover:bg-primary transition-all shadow-xl flex items-center justify-center gap-4"
                    >
                      Өргөдөл илгээх <Send size={16} />
                    </button>
                  </form>
                )}
              </div>
            </section>
          </motion.div>
        ) : (
          <motion.div 
            key="student-tab"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-24"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-12">
                <div className="space-y-6">
                  <h3 className="text-4xl font-serif tracking-tight">Танд тусламж хэрэгтэй юу?</h3>
                  <p className="text-muted text-lg font-medium leading-relaxed">
                    Манай баталгаажсан үе тэнгийн багш нар танд хичээлээ ойлгоход, даалгавраа хийхэд эсвэл шалгалтандаа бэлдэхэд туслахад бэлэн байна.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-white p-8 rounded-[3rem] border border-slate-100 shadow-sm space-y-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary">
                      <MessageSquare size={24} />
                    </div>
                    <h4 className="text-xl font-serif">Шууд чат</h4>
                    <p className="text-xs text-muted font-medium">Багштайгаа шууд холбогдож асуултаа асуугаарай.</p>
                  </div>
                  <div className="bg-white p-8 rounded-[3rem] border border-slate-100 shadow-sm space-y-4">
                    <div className="w-12 h-12 bg-secondary/10 rounded-2xl flex items-center justify-center text-secondary">
                      <Calendar size={24} />
                    </div>
                    <h4 className="text-xl font-serif">Уулзалт товлох</h4>
                    <p className="text-xs text-muted font-medium">Google Meet-ээр ганцаарчилсан уулзалт хийгээрэй.</p>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="aspect-square rounded-[4rem] overflow-hidden shadow-2xl">
                  <img src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=1000&auto=format&fit=crop" className="w-full h-full object-cover" alt="Tutoring" />
                </div>
                <div className="absolute -bottom-10 -right-10 bg-white p-10 rounded-[3rem] shadow-2xl border border-slate-100 space-y-4 max-w-xs">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary">
                      <Star size={20} fill="currentColor" />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-widest">Шилдэг зөвлөхүүд</span>
                  </div>
                  <p className="text-xs font-medium text-muted leading-relaxed italic">
                    "Миний математикийн оноо 20 хувиар өссөн. Үе тэнгийнхнээсээ суралцах нь илүү хялбар байдаг юм байна."
                  </p>
                </div>
              </div>
            </div>

            <section className="bg-slate-900 text-white p-12 md:p-20 rounded-[4rem] shadow-2xl space-y-12">
              <div className="text-center space-y-4">
                <h3 className="text-3xl md:text-5xl font-serif tracking-tight">Зөвлөхөө сонгох</h3>
                <p className="text-white/60 text-lg font-medium max-w-2xl mx-auto">
                  Хичээл бүрээр мэргэшсэн, баталгаажсан үе тэнгийн багш нарын жагсаалт.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[
                  { name: "А. Тэмүүлэн", subject: "Mathematics", rating: 4.9, sessions: 42 },
                  { name: "Б. Наран", subject: "Chemistry", rating: 4.8, sessions: 35 },
                  { name: "Г. Болд", subject: "Physics", rating: 5.0, sessions: 28 }
                ].map((tutor, i) => (
                  <div key={i} className="bg-white/10 backdrop-blur-md border border-white/10 p-10 rounded-[3rem] space-y-8 group hover:bg-white/20 transition-all">
                    <div className="flex justify-between items-start">
                      <div className="w-16 h-16 rounded-2xl bg-white/10 overflow-hidden">
                        <img src={`https://picsum.photos/seed/${tutor.name}/100`} alt={tutor.name} />
                      </div>
                      <div className="flex items-center gap-1 text-amber-400">
                        <Star size={14} fill="currentColor" />
                        <span className="text-xs font-bold">{tutor.rating}</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h4 className="text-2xl font-serif">{tutor.name}</h4>
                      <div className="flex items-center gap-2 text-primary">
                        <BookOpen size={14} />
                        <span className="text-[10px] font-bold uppercase tracking-widest">{tutor.subject}</span>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-white/10 flex justify-between items-center">
                      <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">{tutor.sessions} хичээл</span>
                      <button className="text-[10px] font-bold uppercase tracking-widest text-white hover:text-primary transition-colors flex items-center gap-2">
                        Холбогдох <ArrowRight size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </motion.div>
        )}
      </AnimatePresence>

      <section className="bg-slate-500/5 p-12 md:p-16 border border-slate-500/10 rounded-[5rem] flex flex-col md:flex-row items-center gap-16">
        <div className="w-24 h-24 bg-white border-8 border-secondary rounded-[2.5rem] flex items-center justify-center shrink-0 shadow-lg">
          <Shield size={40} className="text-secondary" />
        </div>
        <div className="space-y-6">
          <h4 className="text-3xl font-serif tracking-tight">Чанарын баталгаа</h4>
          <p className="text-lg text-muted font-medium tracking-tight leading-relaxed max-w-4xl italic">
            Anir нийгэмлэгийн бүх үе тэнгийн багш нар мэдлэгийн шалгалтад тэнцсэн байдаг. Энэ нь сурагчдад үнэн зөв, чанартай мэдээлэл хүрэх баталгаа болдог.
          </p>
        </div>
      </section>
    </div>
  );
};

export default TutorApplication;
